import React from "react";
import { useForm, Controller } from "react-hook-form";
import Input from "../../components/global/Input";
import Button from "../../components/global/Button";
import { useNavigate } from "react-router-dom";
import PhoneNumberInput from "../global/PhoneNumberInput";
import MapComponent from "../global/Map";
import { toast } from "react-toastify";
import { useCreateClubMutation } from "../../lib/redux/services/sections/Clubs";
import { toastMessage } from "../../utils/utils";
import ValidateError from "../global/ValidateError";
import { useGetDictionary } from "../../hooks/useGetDictionary";
import { DictionaryType } from "../../types";

type FormInput = {
  name: string;
  phone_number: string;
  mobile_number: string;
  country_name: string; // New field for country name
  geo_location: string;
  address: string;
  lat: number | null;
  lon: number | null;
};

const FirstClubForm = () => {
  const { Auth, shared, inputs }: DictionaryType = useGetDictionary();
  const navigate = useNavigate();
  const [createClub] = useCreateClubMutation();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormInput>({
    defaultValues: {
      lat: null,
      lon: null,
      country_name: "", // Default value for country name
    },
  });

  const onSubmit = async (formData: FormInput) => {
    if (formData.lat === null || formData.lon === null) {
      alert(shared.select_valid_location_message);
      return;
    }

    try {
      const data = await toast.promise(
        createClub(formData).unwrap(),
        toastMessage()
      );

      navigate(`/auth/club-info-1/${data?.data?.id}`);
    } catch (error) {
      console.error("Login error:", error);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-4 md:space-y-4 max-w-[814px] px-5"
    >
      {/* Row 1: Club Name and Telephone Number */}
      <div className="flex text-start flex-row items-baseline justify-center gap-6 w-full">
        <div className="w-full">
          <Controller
            name="name"
            rules={{ required: true }}
            control={control}
            render={({ field }) => (
              <Input
                inputProps={{
                  ...field,
                  type: "text",
                }}
                label={inputs.club_name}
              />
            )}
          />
          {errors.name && <ValidateError />}
        </div>
        <div className="w-full">
          <Controller
            name="phone_number"
            rules={{ required: false }}
            control={control}
            render={({ field }) => (
              <PhoneNumberInput
                isRequired={false}
                field={field}
                label={inputs.landline_number}
                name="phone_number"
                value={field.value}
                handleChange={(value, countryCode) => {
                  field.onChange(value); // Update mobile number
                }}
              />
            )}
          />
          {errors.phone_number && <ValidateError />}
        </div>
      </div>

      {/* Row 2: Mobile Number and Address */}
      <div className="flex text-start flex-row items-baseline justify-center gap-6 w-full">
        <div className="w-full">
          <Controller
            name="mobile_number"
            rules={{ required: "Mobile Number is required" }}
            control={control}
            render={({ field }) => (
              <PhoneNumberInput
                field={field}
                label={inputs.mobile_number}
                name="mobile_number"
                value={field.value}
                handleChange={(value, countryCode) => {
                  field.onChange(value); // Update mobile number
                  setValue("country_name", countryCode); // Update country name
                }}
              />
            )}
          />
          {errors.mobile_number && <ValidateError />}
        </div>
        <div className="w-full">
          <Controller
            name="address"
            rules={{ required: false }}
            control={control}
            render={({ field }) => (
              <Input
                inputProps={{
                  ...field,
                  type: "text",
                }}
                label={inputs.address}
              />
            )}
          />
          {/* {errors.address && <ValidateError />} */}
        </div>
      </div>

      {/* Row 3: Map (Lat & lon) */}
      <div className="w-full">
        <MapComponent
          setLat={(lat) => setValue("lat", lat)}
          setLng={(lon) => setValue("lon", lon)}
          setGeoLocation={(geoLocation) =>
            setValue("geo_location", geoLocation)
          }
        />
        {(errors.lat || errors.lon) && <ValidateError />}
      </div>

      <div className="flex flex-row justify-center items-center pt-4">
        <Button
          customeStyle="bg-primary text-white w-full rounded-full text-sm py-3 font-bold"
          text={shared.get_started}
          width="max-w-[350px] w-full"
          type="submit"
        />
      </div>
    </form>
  );
};

export default FirstClubForm;
